import { ArrowDown, CircleAlert, CreditCard } from "lucide-react";
import { useId, type ReactNode } from "react";

import { AssetAvatar } from "@/modules/dashboard/crypto-swaps/components/shared/CryptoSwapBits";
import { ConverterAmountInput } from "@/modules/dashboard/crypto-swaps/components/swap/ConverterAmountInput";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { UseCardCopTopUpResult } from "../../hooks/useCardCopTopUp";
import { truncateDecimals, type CopTopUpIssue } from "../../utils/cardCopTopUp";
import { useCopTopUpFormat } from "./copTopUpFormat";

export interface CopTopUpAmountFieldsProps {
  flow: UseCardCopTopUpResult;
  disabled?: boolean;
}

export function CopTopUpAmountFields({ flow, disabled }: CopTopUpAmountFieldsProps) {
  const { t, activeLanguage } = useTranslations("cards");
  const format = useCopTopUpFormat();
  const payId = useId();
  const creditId = useId();
  const payHintId = useId();
  const creditHintId = useId();

  const payValue = flow.anchor === "send" ? flow.amount : (flow.debit ?? "");
  const creditValue =
    flow.anchor === "credit" ? flow.amount : flow.credited ? truncateDecimals(flow.credited, 2) : "";
  const pending = flow.isPricing ? t("cop_top_up_pricing") : "0";
  const payIssue =
    flow.issue && (flow.anchor === "send" || flow.issue.kind === "balance") ? flow.issue : null;
  const creditIssue = flow.issue && !payIssue ? flow.issue : null;

  const issueText = (issue: CopTopUpIssue): string => {
    switch (issue.kind) {
      case "below_min":
        return t("cop_top_up_issue_below_min", { min: format(issue.bound, issue.currency) });
      case "above_max":
        return t("cop_top_up_issue_above_max", { max: format(issue.bound, issue.currency) });
      case "balance":
        return t("cop_top_up_issue_balance");
      case "precision":
        return t("cop_top_up_issue_precision");
      default:
        return t("cop_top_up_issue_invalid");
    }
  };

  return (
    <div className="flex flex-col">
      <AmountRow
        id={payId}
        hintId={payHintId}
        label={t("cop_top_up_pay_label")}
        currency="COP"
        decimals={0}
        locale={activeLanguage}
        value={payValue}
        placeholder={flow.anchor === "send" ? "0" : pending}
        onChange={(value) => flow.editAmount("send", value)}
        invalid={!!payIssue}
        disabled={disabled}
      >
        {payIssue ? (
          <IssueLine>{issueText(payIssue)}</IssueLine>
        ) : (
          <span className="text-xs text-muted-foreground">
            {flow.balance.available
              ? t("cop_top_up_balance", { amount: format(flow.balance.available, "COP") })
              : flow.sendBounds?.min
                ? t("cop_top_up_limits", { min: format(flow.sendBounds.min, "COP") })
                : null}
          </span>
        )}
      </AmountRow>

      <div className="relative z-10 -my-3 flex justify-center" aria-hidden="true">
        <span className="flex size-8 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm">
          <ArrowDown className="size-4" />
        </span>
      </div>

      <AmountRow
        id={creditId}
        hintId={creditHintId}
        label={t("cop_top_up_credit_label")}
        currency="USD"
        decimals={2}
        locale={activeLanguage}
        value={creditValue}
        placeholder={flow.anchor === "credit" ? "0" : pending}
        onChange={(value) => flow.editAmount("credit", value)}
        invalid={!!creditIssue}
        disabled={disabled}
      >
        {creditIssue ? (
          <IssueLine>{issueText(creditIssue)}</IssueLine>
        ) : (
          <span className="text-xs text-muted-foreground">{t("cop_top_up_approx_note")}</span>
        )}
      </AmountRow>
    </div>
  );
}

interface AmountRowProps {
  id: string;
  hintId: string;
  label: string;
  currency: "COP" | "USD";
  decimals: number;
  locale: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  invalid: boolean;
  disabled?: boolean;
  children: ReactNode;
}

function AmountRow({
  id,
  hintId,
  label,
  currency,
  decimals,
  locale,
  value,
  placeholder,
  onChange,
  invalid,
  disabled,
  children,
}: AmountRowProps) {
  return (
    <div
      className={cn(
        "flex flex-col gap-2 rounded-xl border bg-card p-4 transition-colors",
        invalid ? "border-destructive/60" : "border-border focus-within:border-primary/60",
      )}
    >
      <label htmlFor={id} className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      <div className="flex items-center gap-3">
        <ConverterAmountInput
          id={id}
          value={value}
          onValueChange={onChange}
          decimals={decimals}
          minFractionDigits={decimals}
          locale={locale}
          placeholder={placeholder}
          aria-invalid={invalid || undefined}
          aria-describedby={hintId}
          disabled={disabled}
          className="h-auto border-0 bg-transparent p-0 text-2xl font-semibold shadow-none focus-visible:ring-0 aria-invalid:ring-0 md:text-2xl"
        />
        <span className="flex shrink-0 items-center gap-1.5 font-mono text-sm font-medium text-foreground">
          {currency === "COP" ? (
            <AssetAvatar asset="COP" size="sm" />
          ) : (
            <span className="flex size-5 items-center justify-center rounded-full bg-primary/10 text-primary">
              <CreditCard aria-hidden="true" className="size-3" />
            </span>
          )}
          {currency}
        </span>
      </div>
      <div id={hintId}>{children}</div>
    </div>
  );
}

function IssueLine({ children }: { children: ReactNode }) {
  return (
    <span className="flex items-center gap-1.5 text-xs text-destructive">
      <CircleAlert aria-hidden="true" className="size-3.5 shrink-0" />
      {children}
    </span>
  );
}
